import { useEffect, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from './Toast';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { Button } from './Button';

/** Conexión OAuth con Mercado Libre y permiso de «Responder preguntas». Sólo un administrador cambia algo. */

interface ConfigResponse {
  responderPreguntasHabilitado: boolean;
}

export function ConexionMercadoLibre({ esAdmin }: { esAdmin: boolean }) {
  const toast = useToast();
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [fallo, setFallo] = useState(false);
  const [confirmar, setConfirmar] = useState<boolean | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [conectandoMl, setConectandoMl] = useState(false);

  useEffect(() => {
    api.get<ConfigResponse>('/api/v3/ml/stock-bom/config')
      .then(setConfig)
      .catch((e) => { setFallo(true); toast.addToast(extractErrorMessage(e), 'error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Conexión OAuth con MercadoLibre: el backend genera un `state` de un solo uso (vence en 10 min)
  // y el navegador vuelve a esta pantalla después de autorizar.
  const conectarMl = async () => {
    setConectandoMl(true);
    try {
      const { authUrl } = await api.get<{ authUrl: string }>('/api/v3/ml/auth/url');
      window.location.assign(authUrl);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
      setConectandoMl(false);
    }
  };

  const guardar = async (valor: boolean) => {
    setGuardando(true);
    try {
      const c = await api.put<ConfigResponse>('/api/v3/ml/stock-bom/responder-preguntas', { responderPreguntasHabilitado: valor });
      setConfig(c);
      toast.addToast(valor ? 'Responder preguntas habilitado' : 'Responder preguntas deshabilitado', 'success');
      setConfirmar(null);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Encendido por defecto: sólo un false explícito lo apaga (igual que el backend).
  const responderPreguntas = config?.responderPreguntasHabilitado !== false;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Conexión con Mercado Libre</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Abre MercadoLibre para autorizar la cuenta de la tienda. Iniciá sesión con la cuenta del vendedor: el ERP guarda esa
            autorización y vuelve a esta pantalla. El enlace vence a los 10 minutos.
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1">
          <Button variant="secondary" onClick={conectarMl} disabled={!esAdmin || conectandoMl}>
            {conectandoMl ? 'Abriendo Mercado Libre…' : 'Conectar / reconectar Mercado Libre'}
          </Button>
          {!esAdmin && <span className="text-xs text-slate-400">Sólo un administrador puede conectarla</span>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-slate-800">Responder preguntas en Mercado Libre</h2>
            <Badge tone={!config ? 'slate' : responderPreguntas ? 'green' : 'slate'}>{!config ? (fallo ? 'No disponible' : 'Cargando…') : responderPreguntas ? 'Habilitado' : 'Deshabilitado'}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Permite contestar preguntas de compradores desde el ERP. No habilita cambios en publicaciones.
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={responderPreguntas}
              disabled={!esAdmin || !config}
              onChange={() => setConfirmar(!responderPreguntas)}
            />
            Responder preguntas en Mercado Libre
          </label>
          {!esAdmin && <span className="text-xs text-slate-400">Sólo un administrador puede habilitarlo o deshabilitarlo.</span>}
        </div>
      </div>

      <Modal
        open={confirmar !== null}
        onClose={() => setConfirmar(null)}
        title={confirmar ? 'Habilitar responder preguntas en Mercado Libre' : 'Deshabilitar responder preguntas en Mercado Libre'}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            {confirmar
              ? 'Se van a poder contestar preguntas de compradores desde la pantalla de Preguntas. No habilita cambios en publicaciones.'
              : 'Nadie va a poder contestar preguntas desde el ERP hasta que se vuelva a habilitar. Las preguntas se siguen pudiendo ver.'}
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button
              variant={confirmar ? 'primary' : 'danger'}
              loading={guardando}
              onClick={() => confirmar !== null && guardar(confirmar)}
            >
              {confirmar ? 'Sí, activar' : 'Sí, desactivar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
